import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function HumanCheckpoint({ user, setUser, onBack }) {
  const [loading, setLoading] = useState(true);

  const [challenge, setChallenge] = useState(null);
  const [err, setErr] = useState("");

  const [holdCompleted, setHoldCompleted] = useState(false);
  const [patternCompleted, setPatternCompleted] = useState(false);

  const [step, setStep] = useState(1);

  const holdMsTarget = 2400;
  const holdStartRef = useRef(null);
  const holdRafRef = useRef(null);
  const [holdMs, setHoldMs] = useState(0);
  const [holding, setHolding] = useState(false);

  const getToken = () => localStorage.getItem("token");

  const RUNES = useMemo(() => ["sun", "moon", "fire", "water", "leaf", "bolt"], []);
  const RUNE_LABEL = useMemo(
    () => ({
      sun: "☀️",
      moon: "🌙",
      fire: "🔥",
      water: "💧",
      leaf: "🍀",
      bolt: "⚡",
    }),
    []
  );

  const [showPattern, setShowPattern] = useState(true);
  const [pattern, setPattern] = useState([]);
  const [input, setInput] = useState([]);
  const [patternOk, setPatternOk] = useState(false);

  const [powStatus, setPowStatus] = useState({
    running: false,
    progress: 0,
    nonce: null,
    hash: null,
  });
  const powAbortRef = useRef(false);

  useEffect(() => {
    const hardStop = () => stopHold(true);
    window.addEventListener("blur", hardStop);
    document.addEventListener("visibilitychange", hardStop);
    window.addEventListener("pointerup", hardStop);
    window.addEventListener("pointercancel", hardStop);
    return () => {
      window.removeEventListener("blur", hardStop);
      document.removeEventListener("visibilitychange", hardStop);
      window.removeEventListener("pointerup", hardStop);
      window.removeEventListener("pointercancel", hardStop);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setErr("");

        setStep(1);
        setHoldCompleted(false);
        setPatternCompleted(false);
        setHolding(false);
        setHoldMs(0);
        setPowStatus({ running: false, progress: 0, nonce: null, hash: null });
        powAbortRef.current = false;

        const token = getToken();

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/human-check/challenge`,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
            },
          }
        );

        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || "Failed to load verification challenge.");
        if (cancelled) return;

        setChallenge(data);

        const p = Array.isArray(data?.pattern) ? data.pattern : [];
        setPattern(p);
        setShowPattern(true);
        setInput([]);
        setPatternOk(false);

        setTimeout(() => {
          if (!cancelled) setShowPattern(false);
        }, 1200);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Something went wrong.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const stopHold = (hard = false) => {
    setHolding(false);
    holdStartRef.current = null;
    if (holdRafRef.current) cancelAnimationFrame(holdRafRef.current);
    holdRafRef.current = null;

    if (hard) {
      setHoldMs((ms) => (ms >= holdMsTarget ? holdMsTarget : 0));
      return;
    }

    setHoldMs((ms) => (ms >= holdMsTarget ? holdMsTarget : Math.max(0, ms - 400)));
  };

  const startHold = (e) => {
    if (loading) return;
    if (holdCompleted) return;

    e?.preventDefault?.();
    e?.stopPropagation?.();

    setErr("");
    setHolding(true);
    holdStartRef.current = performance.now();

    const tick = () => {
      if (!holdStartRef.current) return;
      const ms = performance.now() - holdStartRef.current;
      const clamped = Math.min(holdMsTarget, ms);
      setHoldMs(clamped);

      if (ms >= holdMsTarget) {
        stopHold(true);
        setHoldMs(holdMsTarget);
        setHoldCompleted(true);
        setStep(2);
        return;
      }

      holdRafRef.current = requestAnimationFrame(tick);
    };

    holdRafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    if (!input.length) {
      setPatternOk(false);
      return;
    }
    if (input.length !== pattern.length) {
      setPatternOk(false);
      return;
    }
    const ok = input.every((v, i) => v === pattern[i]);
    setPatternOk(ok);

    if (ok) {
      setPatternCompleted(true);
      setTimeout(() => setStep(3), 200);
    }
  }, [input, pattern]);

  const tapRune = (r) => {
    if (step !== 2) return;
    if (showPattern) return;
    if (!holdCompleted) return;

    setErr("");
    setInput((prev) => {
      if (prev.length >= pattern.length) return prev;
      return [...prev, r];
    });
  };

  const resetPattern = () => {
    setErr("");
    setInput([]);
    setPatternOk(false);
    setPatternCompleted(false);
  };

  const startPow = async () => {
    if (!challenge?.challengeId || !challenge?.prefix) return null;
    if (!holdCompleted || !patternCompleted) return null;

    setErr("");
    powAbortRef.current = false;
    setPowStatus({ running: true, progress: 0, nonce: null, hash: null });

    const difficulty = Number(challenge?.difficulty ?? 18);
    const prefix = String(challenge.prefix);

    let nonce = 0;
    const start = performance.now();

    while (!powAbortRef.current) {
      for (let i = 0; i < 2500; i++) {
        const msg = `${prefix}:${nonce}`;
        const h = await sha256hex(msg);
        if (hasLeadingZeroBits(h, difficulty)) {
          setPowStatus({ running: false, progress: 1, nonce, hash: h });
          return { nonce, hash: h, ms: performance.now() - start };
        }
        nonce++;
      }

      setPowStatus((s) => ({
        ...s,
        progress: Math.min(0.95, (s.progress || 0) + 0.03),
      }));
      await sleep(0);
    }

    setPowStatus({ running: false, progress: 0, nonce: null, hash: null });
    return null;
  };

  const cancelPow = () => {
    powAbortRef.current = true;
  };

  const submitAll = async () => {
    try {
      if (!challenge?.challengeId) return;

      if (!holdCompleted) {
        setErr("Please complete Step 1 (Hold) first.");
        setStep(1);
        return;
      }
      if (!patternCompleted) {
        setErr("Please complete Step 2 (Runes) first.");
        setStep(2);
        return;
      }

      let pow =
        powStatus?.nonce != null ? { nonce: powStatus.nonce, hash: powStatus.hash } : null;

      if (!pow) {
        const r = await startPow();
        if (!r) {
          setErr("PoW cancelled.");
          return;
        }
        pow = { nonce: r.nonce, hash: r.hash };
      }

      const token = getToken();

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/human-check/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
        },
        body: JSON.stringify({
          challengeId: challenge.challengeId,
          powNonce: pow.nonce,
          powHash: pow.hash,
          input,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Verification failed.");

      if (data?.user && typeof setUser === "function") {
        setUser((u) => ({ ...(u || {}), ...(data.user || {}) }));
      }

      setStep(4);

      setTimeout(() => {
        if (typeof onBack === "function") onBack();
      }, 600);
    } catch (e) {
      setErr(e?.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    if (step === 3 && (!holdCompleted || !patternCompleted)) setStep(!holdCompleted ? 1 : 2);
    if (step === 2 && !holdCompleted) setStep(1);
  }, [step, holdCompleted, patternCompleted]);

  const bg = user?.side === "good" ? "/fight_bg.png" : "/fight_bg.png";

  return (
    <div
      className="shop-container"
      style={{
        boxSizing: "border-box",
        color: "#fff",
        fontFamily: '"Fredoka", sans-serif',
        fontWeight: 600
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <div className="shadow2" style={{ fontSize: 24, letterSpacing: 0.2 }}>Human Checkpoint</div>
        </div>
      </div>

      {loading ? (
        <div style={{ opacity: 0.85, textAlign: "center", marginTop: 40 }}>Loading…</div>
      ) : err ? (
        <div
          style={{
            marginTop: 16,
            padding: 14,
            borderRadius: 14,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.14)",
            color: "#ffb3b3",
            lineHeight: 1.35,
          }}
        >
          {err}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        {[1, 2, 3].map((n) => (
          <div
          className="shadow1"
            key={n}
            style={{
              flex: 1,
              padding: "10px 10px",
              borderRadius: 6,
              textAlign: "center",
              fontSize: 16,
              border: "1px solid rgba(255,255,255,0.12)",
              background:
                step === n
                  ? "linear-gradient(135deg, rgba(255,204,98,0.35), rgba(154,220,251,0.18))"
                  : "rgba(0,0,0,0.28)",
            }}
          >
            {n === 1 ? `STEP 1` : n === 2 ? `STEP 2` : "STEP 3"}
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 12,
          padding: 14,
          borderRadius: 6,
          background: "rgba(0,0,0,0.30)",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {step === 1 && (
            <motion.div
              key="s1"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
            >
              <div className="shadow1" style={{ fontSize: 21, color: "#FFC600" }}>Click Hold To Charge</div>
              <div style={{ marginTop: 14 }}>
                <div
                  style={{
                    height: 12,
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.10)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${Math.round((holdMs / holdMsTarget) * 100)}%`,
                      background: "linear-gradient(90deg, rgba(255,204,98,0.85), rgba(154,220,251,0.75))",
                    }}
                  />
                </div>

                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onPointerDown={startHold}
                  onPointerUp={() => stopHold(false)}
                  onPointerCancel={() => stopHold(true)}
                  onPointerLeave={() => stopHold(false)}
                  style={{
                    marginTop: 14,
                    width: "100%",
                    padding: "14px 14px",
                    borderRadius: 6,
                    border: "1px solid rgba(255,255,255,0.16)",
                    background: holding
                      ? "linear-gradient(135deg, rgba(255,204,98,0.55), rgba(154,220,251,0.25))"
                      : "linear-gradient(135deg, rgba(0,0,0,0.22), rgba(255,255,255,0.06))",
                    color: "white",
                    fontWeight: 900,
                    fontSize: 15,
                    textShadow: "1px 1px rgba(0,0,0,0.6)",
                    cursor: holdCompleted ? "default" : "pointer",
                    userSelect: "none",
                    touchAction: "none",
                    opacity: holdCompleted ? 0.85 : 1,
                  }}
                  disabled={holdCompleted}
                >
                  {holdCompleted ? "Charged ✅" : holding ? "Holding…" : "Hold to Charge"}
                </motion.button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="s2"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
            >
              <div style={{ fontSize: 21, color: "#FFC600" }}>Repeat The Sequence</div>

              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <div
                  style={{
                    fontSize: 26,
                    letterSpacing: 6,
                    marginTop: 0,
                    filter: showPattern ? "none" : "blur(4px)",
                  }}
                >
                  {pattern.map((r) => RUNE_LABEL[r] || r).join(" ")}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 12 }}>
                {RUNES.map((r) => (
                  <button
                    key={r}
                    onClick={() => tapRune(r)}
                    disabled={showPattern}
                    style={{
                      padding: "8px 0",
                      borderRadius: 16,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(0,0,0,0.28)",
                      color: "white",
                      fontWeight: 900,
                      fontSize: 22,
                      cursor: showPattern ? "not-allowed" : "pointer",
                      opacity: showPattern ? 0.55 : 1,
                    }}
                  >
                    {RUNE_LABEL[r] || r}
                  </button>
                ))}
              </div>

              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 14,
                  background: "rgba(0,0,0,0.22)",
                  border: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <div style={{ marginTop: 6, fontSize: 20, letterSpacing: 4 }}>
                  {(input.length ? input : ["…"]).map((r) => RUNE_LABEL[r] || r).join(" ")}
                </div>

                <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                  <button
                    onClick={resetPattern}
                    style={{
                      flex: 1,
                      padding: "12px 12px",
                      borderRadius: 14,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(0,0,0,0.26)",
                      color: "white",
                      fontWeight: 900,
                      cursor: "pointer",
                    }}
                  >
                    Reset
                  </button>
                  <div
                    style={{
                      flex: 1,
                      padding: "12px 12px",
                      borderRadius: 14,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: patternOk ? "rgba(124,252,0,0.14)" : "rgba(255,255,255,0.06)",
                      textAlign: "center",
                      fontWeight: 900,
                    }}
                  >
                    {patternOk ? "Matched ✅" : `${input.length}/${pattern.length}`}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="s3"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
            >
              <div style={{ fontSize: 21, color: "#FFC600" }}>Submit Result</div>

              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <div style={{ marginTop: 10, height: 10, borderRadius: 999, background: "rgba(255,255,255,0.10)", overflow: "hidden" }}>
                  <div
                    style={{
                      height: "100%",
                      width: `${Math.round((powStatus.progress || 0) * 100)}%`,
                      background: "linear-gradient(90deg, rgba(154,220,251,0.8), rgba(255,204,98,0.8))",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                  <button
                    onClick={submitAll}
                    disabled={powStatus.running}
                    style={{
                      flex: 1,
                      padding: "12px 12px",
                      borderRadius: 14,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "linear-gradient(135deg, rgba(255,204,98,0.75), rgba(154,220,251,0.35))",
                      color: "white",
                      fontWeight: 900,
                      cursor: powStatus.running ? "not-allowed" : "pointer",
                      opacity: powStatus.running ? 0.65 : 1,
                    }}
                  >
                    Verify & Unlock
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div
              key="s4"
              initial={{ opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.22 }}
              style={{ textAlign: "center" }}
            >
              <div style={{ fontSize: 64, marginTop: 0 }}>✨</div>
              <div style={{ fontSize: 28, fontWeight: 300, marginTop: 0, marginBottom: 20 }}>Checkpoint Cleared!</div>

              <div
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 14,
                  background: "rgba(124,252,0,0.10)",
                  border: "1px solid rgba(124,252,0,0.20)",
                  fontWeight: 300,
                  fontSize: 21,
                }}
              >
                Reward: <span style={{ color: "#FFC600" }}>+{Number(challenge?.rewardSparks ?? 0)} Sparks</span>
              </div>

              <button
                onClick={onBack}
                style={{
                  marginTop: 14,
                  width: "100%",
                  padding: "14px 14px",
                  borderRadius: 16,
                  border: "1px solid rgba(255,255,255,0.16)",
                  background: "linear-gradient(135deg, rgba(255,204,98,0.75), rgba(154,220,251,0.35))",
                  color: "white",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Continue
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

async function sha256hex(text) {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest("SHA-256", enc.encode(text));
  const arr = Array.from(new Uint8Array(buf));
  return arr.map((b) => b.toString(16).padStart(2, "0")).join("");
}

function hasLeadingZeroBits(hex, bits) {
  const fullNibbles = Math.floor(bits / 4);
  const rem = bits % 4;

  for (let i = 0; i < fullNibbles; i++) {
    if (hex[i] !== "0") return false;
  }
  if (rem === 0) return true;

  const nextNibble = parseInt(hex[fullNibbles], 16);
  const threshold = 1 << (4 - rem);
  return nextNibble < threshold;
}